import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { countries } from 'country-data';
import {
  Modal,
  Form,
  Grid,
  Button,
  Icon,
  Input,
  Dropdown,
  Message,
  Tab,
  Checkbox,
  Progress,
} from 'semantic-ui-react';

import HashtagsInput from '../hashtags-input';
import { DYZIO_CURRENCIES } from '../../utils/helpers/currencies';
import { transformHashtagstIntoArray } from '../../utils/hashtags-utils';
import { getInfluencerFromOrganisations } from '../../utils/helper-functions';
import createNewCampaign from '../../utils/create-new-campaign';
import hasRESTClient from '../../hocs/has-rest-client';

const mutedStyle = { color: 'gray' };
const dropdownStyle = { marginTop: 8, marginBottom: 8 };
const paneStyle = { border: 0, padding: 0, boxShadow: 'none' };
const creatorListStyle = {
  maxHeight: 280,
  overflowY: 'auto',
  borderTop: '1px solid #f0f0f0',
};
const creatorRowStyle = { padding: '8px 0', borderBottom: '1px solid #f5f5f5' };
const masterRowStyle = {
  padding: '8px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const RequiredLabel = () => <span style={{ color: 'red' }}>*</span>;

const COUNTRY_OPTIONS = [
  {
    text: '<<< No Country >>>',
    style: mutedStyle,
    key: 'no-country',
    value: null,
  },
  ..._(countries.all)
    .reject((c) => _.isEmpty(c.emoji))
    .map((c) => ({
      text: `${c.emoji} ${c.name}`,
      key: c.alpha2,
      value: c.alpha2,
    }))
    .value(),
];

const safeDateAt15 = (date) =>
  date && moment(date).isValid()
    ? moment(date).set({ hour: 15, minute: 15 })
    : null;

const getCreatorOrganisation = (discussion) => {
  const orgs = _.get(discussion, 'organisations') || [];
  return _.find(orgs, (o) => o && o.influencer) || _.find(orgs, (o) => o && o.id);
};

const buildCreatorFromDiscussion = (discussion) => {
  const influencer =
    discussion.influencer ||
    getInfluencerFromOrganisations(discussion.organisations);
  const name = discussion.name || _.get(influencer, 'name') || '';
  const email = _.get(influencer, 'email') || '';
  if (!name && !email) return null;
  const handle =
    _.get(influencer, 'primarySocialHandle') ||
    _.get(influencer, 'instagram') ||
    _.get(influencer, 'tiktok') ||
    _.get(influencer, 'youtube') ||
    _.get(influencer, 'twitter') ||
    '';
  const creatorOrg = getCreatorOrganisation(discussion);
  return {
    id: discussion.id,
    name,
    email,
    handle,
    instagram: _.get(influencer, 'instagram') || '',
    primarySocialHandle: _.get(influencer, 'primarySocialHandle') || '',
    influencerId: _.get(influencer, 'id'),
    organisationId: _.get(creatorOrg, 'id'),
  };
};

// Create discussions directly on the new campaign — same pattern as the
// CSV uploader in dyzio-integration-api. Skipping the invitation path
// avoids the invitation-processor Lambda race that drops ~1 discussion
// from campaign.discussions per run. Tradeoffs: no invite email is sent
// and the new discussions share a placeholder invitation reference.
const PLACEHOLDER_INVITATION_ID = 'dc2475b2-c6a2-468c-9ee0-ce34cd202565';

const buildDiscussionPayload = (creator, newCampaignId) => {
  const attributes = {
    name: creator.name,
    discussionType: 'REPORTING',
    campaign: { type: 'campaigns', id: newCampaignId },
    invitations: [{ type: 'campaignInvitation', id: PLACEHOLDER_INVITATION_ID }],
    budget: 0,
    budgetStatus: 'NONE',
    commission: 0,
    isScanningCompleted: false,
    workflowStatus: 'NOT_CONTACTED',
    reviewEmailSent: false,
    createdDate: new Date(),
  };
  if (creator.influencerId) {
    attributes.influencer = { type: 'influencer', id: creator.influencerId };
  }
  if (creator.organisationId) {
    attributes.organisations = [{ type: 'organisations', id: creator.organisationId }];
  }
  return { data: { attributes } };
};

const addDiscussionToNewCampaign = async (restClient, newCampaignId, creator) => {
  const created = await restClient.post(
    '/discussions',
    buildDiscussionPayload(creator, newCampaignId),
  );
  const newDiscussionId = _.get(created, 'data.data.id');
  if (!newDiscussionId) throw new Error('Discussion create returned no id');
  await restClient.post(
    `/campaigns/${newCampaignId}/relationships/discussions/`,
    { data: { type: 'discussions', id: newDiscussionId } },
  );
  return newDiscussionId;
};

// Run creators serially with a small pause. Even though we're no longer
// racing the invitation Lambda, two of our own attach POSTs landing
// simultaneously on the same campaign.discussions array would race the
// fetch-modify-save handler. Concurrency=1 + pause keeps both writers
// well separated.
const DISCUSSION_CONCURRENCY = 1;
const DISCUSSION_PAUSE_MS = 300;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const inBatches = async (items, batchSize, fn) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    // eslint-disable-next-line no-await-in-loop
    const batchResults = await Promise.all(batch.map(fn));
    results.push(...batchResults);
    if (i + batchSize < items.length) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(DISCUSSION_PAUSE_MS);
    }
  }
  return results;
};

const isLocalhost = () => {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '';
};
const track = (name, params) => {
  if (window.gtag && !isLocalhost()) window.gtag('event', name, params);
};

class DuplicateCampaignModal extends Component {
  constructor(props) {
    super(props);
    const { sourceCampaign } = props;
    const sourceName = _.get(sourceCampaign, 'name', '');
    this.state = {
      name: sourceName ? `${sourceName} (duplicate)` : '',
      brand: _.get(sourceCampaign, 'brand') || '',
      budget: _.get(sourceCampaign, 'budget', ''),
      currency: _.get(sourceCampaign, 'currency') || null,
      hashtags: _.get(sourceCampaign, 'hashtags') || '',
      country: _.get(sourceCampaign, 'country') || null,
      liveDate: sourceCampaign.liveDate
        ? moment(sourceCampaign.liveDate)
        : null,
      endDate: sourceCampaign.endDate ? moment(sourceCampaign.endDate) : null,
      creators: _(_.get(sourceCampaign, 'discussions', []))
        .map(buildCreatorFromDiscussion)
        .compact()
        .value(),
      selectedCreatorIds: new Set(),
      creatorSearch: '',
      submitting: false,
      inviteProgress: null,
      error: null,
      noMentionsWarned: false,
    };
  }

  onFieldChanged = (event, { name, value }) => this.setState({ [name]: value });
  onLiveDateChange = (date) => this.setState({ liveDate: date });
  onEndDateChange = (date) => this.setState({ endDate: date });

  onToggleCreator = (id) =>
    this.setState((prev) => {
      const next = new Set(prev.selectedCreatorIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selectedCreatorIds: next };
    });

  onToggleAll = () =>
    this.setState((prev) => {
      const allSelected =
        prev.creators.length > 0 &&
        prev.selectedCreatorIds.size === prev.creators.length;
      return {
        selectedCreatorIds: allSelected
          ? new Set()
          : new Set(_.map(prev.creators, (c) => c.id)),
      };
    });

  isSubmitDisabled = () => {
    const { name, hashtags, budget, liveDate, endDate, submitting } =
      this.state;
    return (
      submitting ||
      _.isEmpty(name) ||
      _.isEmpty(hashtags) ||
      _.isEmpty(_.toString(budget)) ||
      !liveDate ||
      !endDate
    );
  };

  hasMention = () =>
    _.some(_.split(this.state.hashtags, ','), (t) =>
      _.startsWith(_.trim(t), '@'),
    );

  onSubmit = () => {
    if (!this.hasMention() && !this.state.noMentionsWarned) {
      this.setState({ noMentionsWarned: true });
      return undefined;
    }
    return this.doSubmit();
  };

  doSubmit = async () => {
    if (this.inflight) return;
    this.inflight = true;

    const {
      name,
      brand,
      budget,
      currency,
      hashtags,
      country,
      liveDate,
      endDate,
      creators,
      selectedCreatorIds,
    } = this.state;
    const { jsonAPIClient, userInfo, organisation, sourceCampaign } =
      this.props;

    this.setState({ submitting: true, error: null });

    const startedAt = Date.now();
    const sourceCampaignId = _.get(sourceCampaign, 'id');
    const organisationId = _.get(userInfo, 'organisationId');
    const selected = _.filter(creators, (c) => selectedCreatorIds.has(c.id));

    track('duplicate_campaign_started', {
      source_campaign_id: sourceCampaignId,
      organisation_id: organisationId,
      total_source_creators: creators.length,
      selected_count: selected.length,
      has_mention: this.hasMention(),
    });

    let newCampaignId;
    try {
      newCampaignId = await createNewCampaign({
        name,
        brand,
        budget,
        currency,
        hashtags,
        country,
        liveDate,
        endDate,
        campaignType: _.get(sourceCampaign, 'campaignType', 'AUTOMATED'),
        scanUnauthorisedStories: _.get(organisation, 'isStickerScannerEnabled'),
        useEstimatedReachAndImpressions: false,
        jsonAPIClient,
        userInfo,
        organisation,
        skipRedirect: true,
      });
    } catch (e) {
      // Create failed — no campaign exists, surface the error and let the user retry.
      this.inflight = false;
      this.setState({
        submitting: false,
        inviteProgress: null,
        error: _.get(
          e,
          'message',
          'Could not duplicate the campaign. Please try again.',
        ),
      });
      track('duplicate_campaign_failed', {
        source_campaign_id: sourceCampaignId,
        organisation_id: organisationId,
        selected_count: selected.length,
        duration_ms: Date.now() - startedAt,
        error_message: _.get(e, 'message', 'unknown'),
      });
      return;
    }

    // Campaign exists from here on. Discussion failures are logged but do NOT
    // abort the redirect — otherwise the user sees an error and re-clicks
    // Duplicate, creating a second campaign.
    let discussionResults = [];
    if (!_.isEmpty(selected)) {
      this.setState({ inviteProgress: { done: 0, total: selected.length } });
      const bumpProgress = () =>
        this.setState((prev) => ({
          inviteProgress: prev.inviteProgress
            ? { ...prev.inviteProgress, done: prev.inviteProgress.done + 1 }
            : null,
        }));
      const { restClient } = this.props;
      discussionResults = await inBatches(selected, DISCUSSION_CONCURRENCY, (creator) =>
        addDiscussionToNewCampaign(restClient, newCampaignId, creator)
          .catch((err) => {
            console.error(
              'Failed to add discussion for creator on duplicated campaign',
              err,
            ); // eslint-disable-line no-console
            return null;
          })
          .then((result) => {
            bumpProgress();
            return result;
          }),
      );
    }

    const discussionSuccessCount = _.filter(discussionResults, (r) => r !== null).length;
    const discussionFailureCount = discussionResults.length - discussionSuccessCount;
    track('duplicate_campaign_completed', {
      source_campaign_id: sourceCampaignId,
      new_campaign_id: newCampaignId,
      organisation_id: organisationId,
      selected_count: selected.length,
      discussion_success_count: discussionSuccessCount,
      discussion_failure_count: discussionFailureCount,
      duration_ms: Date.now() - startedAt,
    });

    window.location = `/campaign/${newCampaignId}`;
  };

  renderDetailsPane() {
    const {
      name,
      brand,
      budget,
      currency,
      hashtags,
      country,
      liveDate,
      endDate,
    } = this.state;
    const safeLive = safeDateAt15(liveDate);
    const safeEnd = safeDateAt15(endDate);

    return (
      <Form>
        <Form.Field>
          <label>
            Campaign name
            <RequiredLabel />
          </label>
          <Form.Input
            autoFocus
            name="name"
            value={name}
            onChange={this.onFieldChanged}
          />
        </Form.Field>

        <Form.Group widths="equal">
          <Form.Field>
            <label>Brand</label>
            <Form.Input
              name="brand"
              value={brand}
              onChange={this.onFieldChanged}
            />
          </Form.Field>

          <Form.Field>
            <label>Country (optional)</label>
            <Dropdown
              fluid
              search
              selection
              name="country"
              placeholder="e.g United Kingdom"
              value={country}
              options={COUNTRY_OPTIONS}
              onChange={this.onFieldChanged}
            />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field>
            <label>Currency</label>
            <Dropdown
              fluid
              search
              selection
              name="currency"
              style={dropdownStyle}
              placeholder="Select currency"
              value={currency}
              options={DYZIO_CURRENCIES}
              onChange={this.onFieldChanged}
            />
          </Form.Field>

          <Form.Field>
            <label>
              Total Budget
              <RequiredLabel />
            </label>
            <Form.Input
              type="number"
              name="budget"
              value={budget}
              onChange={this.onFieldChanged}
            />
          </Form.Field>
        </Form.Group>

        <Form.Group widths="equal">
          <Form.Field>
            <label>
              Live Date
              <RequiredLabel />
            </label>
            <DatePicker
              onChange={this.onLiveDateChange}
              selected={safeLive}
              dateFormat="MMM Do YYYY"
              selectsStart
              endDate={safeEnd}
              maxDate={safeEnd}
              popperPlacement="top-start"
            />
          </Form.Field>

          <Form.Field>
            <label>
              End Date
              <RequiredLabel />
            </label>
            <DatePicker
              onChange={this.onEndDateChange}
              selected={safeEnd}
              dateFormat="MMM Do YYYY"
              selectsEnd
              startDate={safeLive}
              minDate={safeLive}
              popperPlacement="top-start"
            />
          </Form.Field>
        </Form.Group>

        <HashtagsInput
          onValueChanged={this.onFieldChanged}
          campaignType="AUTOMATED"
          hashtags={transformHashtagstIntoArray(hashtags)}
        />
      </Form>
    );
  }

  renderCreatorsPane() {
    const { creators, selectedCreatorIds, creatorSearch } = this.state;

    if (_.isEmpty(creators)) {
      return (
        <p style={{ ...mutedStyle, padding: '24px 0', textAlign: 'center' }}>
          No creators on the source campaign.
        </p>
      );
    }

    const q = _.trim(_.toLower(creatorSearch));
    const visible = q
      ? _.filter(
          creators,
          (c) =>
            _.includes(_.toLower(c.name || ''), q) ||
            _.includes(_.toLower(c.email || ''), q) ||
            _.includes(_.toLower(c.handle || ''), q),
        )
      : creators;

    const total = creators.length;
    const selectedCount = selectedCreatorIds.size;
    const allSelected = selectedCount === total;
    const someSelected = selectedCount > 0 && !allSelected;

    return (
      <div>
        <Input
          fluid
          icon="search"
          iconPosition="left"
          placeholder="Search by name, email, or @handle"
          value={creatorSearch}
          onChange={(e, { value }) => this.setState({ creatorSearch: value })}
          style={{ marginBottom: 8 }}
        />
        <div style={masterRowStyle}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={this.onToggleAll}
            label={
              <label>
                <strong>Select all</strong>
              </label>
            }
          />
          <span style={mutedStyle}>
            {selectedCount} of {total} selected
          </span>
        </div>
        <div style={creatorListStyle}>
          {_.isEmpty(visible) ? (
            <p
              style={{ ...mutedStyle, padding: '24px 0', textAlign: 'center' }}
            >
              No creators match “{creatorSearch}”.
            </p>
          ) : (
            _.map(visible, (c) => (
              <div key={c.id} style={creatorRowStyle}>
                <Checkbox
                  checked={selectedCreatorIds.has(c.id)}
                  onChange={() => this.onToggleCreator(c.id)}
                  label={
                    <label>
                      <strong>{c.name || c.email || 'Unnamed creator'}</strong>
                      {c.email && <span style={mutedStyle}> — {c.email}</span>}
                      {c.handle && (
                        <span style={mutedStyle}> · @{c.handle}</span>
                      )}
                    </label>
                  }
                />
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  render() {
    const { submitting, error, creators, selectedCreatorIds, inviteProgress } =
      this.state;
    const { onClose } = this.props;
    const showNoMentionsWarning =
      this.state.noMentionsWarned && !this.hasMention();
    const creatorsTabLabel = `Creators (${selectedCreatorIds.size}/${creators.length})`;

    const panes = [
      {
        menuItem: 'Details',
        render: () => (
          <Tab.Pane attached={false} style={paneStyle}>
            {this.renderDetailsPane()}
          </Tab.Pane>
        ),
      },
      {
        menuItem: creatorsTabLabel,
        render: () => (
          <Tab.Pane attached={false} style={paneStyle}>
            {this.renderCreatorsPane()}
          </Tab.Pane>
        ),
      },
    ];

    return (
      <Modal
        open
        closeOnEscape
        closeOnDimmerClick={!submitting}
        onClose={submitting ? undefined : onClose}
        size="small"
      >
        <Modal.Header>Duplicate campaign</Modal.Header>
        <Modal.Content scrolling>
          {submitting ? (
            <div
              style={{
                padding: '72px 32px 88px',
                textAlign: 'center',
                minHeight: 320,
              }}
            >
              <img
                src="/icons/dyzio-icon.svg"
                alt="Loading…"
                style={{
                  width: 72,
                  height: 72,
                  animationName: 'spin',
                  animationDuration: '1.25s',
                  animationIterationCount: 'infinite',
                  animationTimingFunction: 'linear',
                }}
              />
              <div
                style={{
                  marginTop: 32,
                  fontSize: 16,
                  color: '#555',
                }}
              >
                Duplicating…
              </div>
              {inviteProgress && (
                <div style={{ maxWidth: 320, margin: '32px auto 0' }}>
                  <Progress
                    active
                    value={inviteProgress.done}
                    total={inviteProgress.total}
                    size="tiny"
                    color="pink"
                  />
                </div>
              )}
            </div>
          ) : (
            <Tab menu={{ secondary: true, pointing: true }} panes={panes} />
          )}
          {!submitting && showNoMentionsWarning && (
            <Message warning>
              <Message.Header>
                <Icon name="warning circle" />
                No @mention tracked
              </Message.Header>
              <p>
                Your campaign has zero @mentions. Click{' '}
                <strong>Duplicate campaign</strong> again to proceed anyway.
              </p>
            </Message>
          )}
          {error && (
            <Message negative>
              <Message.Header>
                <Icon name="warning circle" />
                Couldn’t duplicate
              </Message.Header>
              <p>{error}</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Grid>
            <Grid.Row>
              <Grid.Column width={8}>
                <Button
                  fluid
                  basic={false}
                  disabled={submitting}
                  onClick={onClose}
                >
                  <Icon name="close" />
                  Cancel
                </Button>
              </Grid.Column>
              <Grid.Column width={8}>
                <Button
                  fluid
                  basic={false}
                  color="pink"
                  disabled={this.isSubmitDisabled()}
                  onClick={this.onSubmit}
                >
                  <Icon name="copy" />
                  {submitting ? 'Duplicating…' : 'Duplicate campaign'}
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Actions>
      </Modal>
    );
  }
}

DuplicateCampaignModal.propTypes = {
  sourceCampaign: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  jsonAPIClient: PropTypes.object.isRequired,
  restClient: PropTypes.func.isRequired,
  userInfo: PropTypes.object.isRequired,
  organisation: PropTypes.object,
};

DuplicateCampaignModal.defaultProps = {
  organisation: null,
};

export default hasRESTClient(DuplicateCampaignModal);



/** WEBPACK FOOTER **
 ** ./src/components/duplicate-campaign-modal/index.js
 **/